import React, { useEffect, useRef } from "react";
import { Col, Form, Row } from "react-bootstrap";
import Select, { components, InputActionMeta, SingleValue } from "react-select";

interface OptionType {
  value: any;
  label: string;
}

interface SelectGroupProps {
  controlId: string;
  label: string;
  icon: string;
  options: OptionType[];
  valueType?: OptionType | null;
  onChange?: (selectedOption: OptionType | null) => void;
  handleCategoryChange?: (selectedOption: OptionType | null) => void;
  placeholder?: string;
  noOptionsMessage?: string;
  name?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  disabled?: boolean;
}

const SelectGroup: React.FC<SelectGroupProps> = ({
  controlId,
  label,
  icon,
  options,
  valueType,
  onChange,
  handleCategoryChange,
  placeholder,
  noOptionsMessage,
  name,
  isSearchable = true,
  isClearable = true,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = React.useState<string>("");
  const [selectedOption, setSelectedOption] = React.useState<OptionType | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  // 🔄 Synchronisation avec valeur externe
  React.useEffect(() => {
    // valeur valide seulement si valueType est objet et value != null
    const safeValueType =
      valueType && typeof valueType === "object" && valueType.value != null
        ? valueType
        : null;

    if (safeValueType) {
      const found = options.find(
        (opt) => String(opt.value) === String(safeValueType.value)
      );
      if (found) {
        setSelectedOption(found);
        setInputValue(found.label || "");
        return;
      }
      setSelectedOption(safeValueType);
      setInputValue(safeValueType.label || "");
    } else {
      setSelectedOption(null);
      setInputValue("");
 
    }
  }, [valueType, options]);


  // 🔁 Mise à jour si options changent
  React.useEffect(() => {
    if (selectedOption) {
      const matched = options.find(
        (opt) => String(opt.value) === String(selectedOption.value)
      );
      if (matched && matched !== selectedOption) {
        setSelectedOption(matched);
        setInputValue(matched.label || "");
      }
    }
  }, [options]);

  const DropdownIndicator = (props: any) =>
    components.DropdownIndicator ? (
      <components.DropdownIndicator {...props}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <i className={`las la-${icon}`} style={{ marginRight: "8px" }} />
          {props.children}
        </div>
      </components.DropdownIndicator>
    ) : null;

  const renderNoOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    return noOptionsMessage
      ? noOptionsMessage.replace("{inputValue}", inputValue)
      : `Aucune option pour ${inputValue}`;
  };

  // 🔧 FX DOM : observer pour forcer input visible
  useEffect(() => {
    if (!selectRef.current) return;

    const inputEl = selectRef.current.querySelector<HTMLInputElement>(".react-select__input");
    if (!inputEl) return;

    // Force initial
    inputEl.style.opacity = "1";
    inputEl.style.color = "#000";
    inputEl.style.visibility = "visible";

    // Observer pour tous les changements inline
    const observer = new MutationObserver(() => {
      inputEl.style.opacity = "1";
      inputEl.style.color = "#000";
      inputEl.style.visibility = "visible";
    });

    observer.observe(inputEl, { attributes: true, attributeFilter: ["style"] });

    return () => observer.disconnect();
  }, []);

  const forceInputVisible = () => {
    if (!selectRef.current) return;
    const inputEl = selectRef.current.querySelector<HTMLInputElement>(".react-select__input");
    if (inputEl) {
      inputEl.style.opacity = "1";
      inputEl.style.color = "#000";
      inputEl.style.visibility = "visible";
    }
  };

  return (
    <Form.Group controlId={controlId} as={Row} style={{ marginTop: "15px" }} ref={selectRef}>
      <Form.Label column sm="4" md="4" lg="4" className="text-md-end text-left">
        <strong>{label}</strong>
      </Form.Label>
      <Col sm="8" md="8" lg="8">
        <Select
          classNamePrefix="react-select"
          options={options}
          value={selectedOption ?? null}
          inputValue={inputValue}
          onInputChange={(val: string, meta: InputActionMeta) => {
            if (meta.action === "input-change") {
              setInputValue(val);
              forceInputVisible();
              return;
            }
            if (meta.action === "set-value" || meta.action === "menu-close") {
              const labelToRestore = selectedOption?.label || "";
              setInputValue(labelToRestore);
              forceInputVisible();
            }
          }}
          onChange={(option: SingleValue<OptionType>) => {
            const opt = (option as OptionType) || null;
            const canonical = opt
              ? options.find((o) => String(o.value) === String(opt.value)) || opt
              : null;
            setSelectedOption(canonical);
            setInputValue(canonical?.label || "");
            forceInputVisible();
            
           
            (selectRef.current?.querySelector(".react-select__control") as HTMLElement)?.click();


         

            if (onChange) onChange(canonical);
            else if (handleCategoryChange) handleCategoryChange(canonical);
          }}
          getOptionLabel={(opt: OptionType) => opt.label}
          getOptionValue={(opt: OptionType) => String(opt.value)}
          isSearchable={isSearchable}
          isClearable={isClearable}
          isDisabled={disabled}
          components={{ DropdownIndicator }}
          className="w-100"
          id={name}
          name={name}
          placeholder={"Sélectionnez une option"}
          noOptionsMessage={({ inputValue }) => renderNoOptionsMessage({ inputValue })}
          styles={{
            menu: (base) => ({ ...base, zIndex: 9999 }),
            singleValue: (base) => ({ ...base, color: "#000" }),
            placeholder: (base) => ({ ...base, color: "#000" }),
          }}
          onMenuOpen={forceInputVisible}
          onMenuClose={forceInputVisible}
        />
      </Col>
    </Form.Group>
  );
};

export default SelectGroup;
